import { useMemo, type ReactNode } from "react"
import { AlertTriangle, CalendarDays, ChevronRight, MoreHorizontal } from "lucide-react"

import type { EventsListItem } from "components/mentorship/EventsListCard"
import { isCurrentUserMeetingParticipant } from "components/mentorship/meetingInvitation"
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar"
import { Button } from "components/ui/button"
import type { Person } from "components/mentorship/admin/types"
import { cn } from "lib/utils"
import { meetingTimeZoneLabel, resolveMeetingTimeZone } from "utils/meetingTime"

import "./cohort-experience.css"

const DAY_MS = 24 * 60 * 60 * 1000

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(Math.max(value, minimum), maximum)

const validDate = (value?: Date | string | null) => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export const isCohortEventLive = (event?: EventsListItem | null, now = Date.now()) => {
  if (!event || event.status === "cancelled" || event.manuallyEndedAt) return false
  const start = validDate(event.startTimeIso)
  if (!start || start.getTime() > now) return false
  const end = validDate(event.endTimeIso)
  const stillInScheduledWindow = !end || now < end.getTime()
  const activeOverrun = Boolean(event.canJoinVideo && event.activeParticipantCount && event.activeParticipantCount > 0)
  return stillInScheduledWindow || activeOverrun
}

export type CohortTimingState = "missing" | "upcoming" | "active" | "finished"

export const getCohortTiming = (start?: Date | null, end?: Date | null, now = Date.now()) => {
  if (!start || !end || end.getTime() <= start.getTime()) {
    return {
      label: "Dates not set",
      detail: null as string | null,
      progress: null as number | null,
      totalWeeks: null as number | null,
      weeksLeft: null as number | null,
      state: "missing" as CohortTimingState,
    }
  }
  const totalWeeks = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (7 * DAY_MS)))
  if (now < start.getTime()) {
    const weeksUntilStart = Math.max(1, Math.ceil((start.getTime() - now) / (7 * DAY_MS)))
    return {
      label: `Starts in ${weeksUntilStart} ${weeksUntilStart === 1 ? "week" : "weeks"}`,
      detail: null,
      progress: 0,
      totalWeeks,
      weeksLeft: totalWeeks,
      state: "upcoming" as CohortTimingState,
    }
  }
  if (now > end.getTime()) {
    const weeksSinceEnd = Math.max(1, Math.floor((now - end.getTime()) / (7 * DAY_MS)))
    return {
      label: `Finished · ${totalWeeks} ${totalWeeks === 1 ? "week" : "weeks"}`,
      detail: `Ended ${weeksSinceEnd} ${weeksSinceEnd === 1 ? "week" : "weeks"} ago`,
      progress: 100,
      totalWeeks,
      weeksLeft: 0,
      state: "finished" as CohortTimingState,
    }
  }
  const week = clamp(Math.floor((now - start.getTime()) / (7 * DAY_MS)) + 1, 1, totalWeeks)
  const weeksLeft = Math.max(0, totalWeeks - week)
  const progress = clamp(((now - start.getTime()) / (end.getTime() - start.getTime())) * 100, 0, 100)
  return {
    label: `Week ${week} of ${totalWeeks} · ${weeksLeft} left`,
    detail: null,
    progress,
    totalWeeks,
    weeksLeft,
    state: "active" as CohortTimingState,
  }
}

const formatTimelineDate = (date: Date) => date.toLocaleDateString("en-US", { month: "short", day: "numeric" })

type CohortHeaderProps = {
  name?: string | null
  startDate?: Date | null
  endDate?: Date | null
  peopleCount?: number
  people?: Person[]
  context?: string | null
  actions?: ReactNode
  onOpenMenu?: () => void
  className?: string
}

export function CohortHeader({
  name,
  startDate,
  endDate,
  peopleCount,
  people = [],
  context,
  actions,
  onOpenMenu,
  className,
}: CohortHeaderProps) {
  const timing = useMemo(() => getCohortTiming(startDate, endDate), [endDate, startDate])
  const peopleLabel = typeof peopleCount === "number" ? `${peopleCount} ${peopleCount === 1 ? "person" : "people"}` : null
  const dateRange = startDate && endDate
    ? `${startDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })} — ${endDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
    : null
  const desktopMeta = [timing.label, timing.detail, dateRange, context].filter(Boolean).join(" · ")
  const visiblePeople = people.slice(0, 3)

  return (
    <header className={cn("cohort-member-header cohort-mobile-bleed min-w-0 border-b border-[var(--cohort-rule)] pb-[18px]", className)}>
      <div className="flex min-w-0 items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="break-words text-[23px] leading-[1.2] tracking-[-0.01em] text-[var(--cohort-ink)] sm:text-[38px] sm:leading-[1.1] sm:tracking-[-0.02em]">
            {name || "Your cohort"}
          </h1>
          <p className="mt-2 flex items-center gap-3 text-sm text-[var(--cohort-ink-muted)] sm:hidden">
            <span>{timing.label}</span>
            {timing.detail ? <><span className="h-3 w-px shrink-0 bg-[var(--cohort-rule)]" aria-hidden="true" /><span>{timing.detail}</span></> : null}
            {peopleLabel ? <><span className="h-3 w-px shrink-0 bg-[var(--cohort-rule)]" aria-hidden="true" /><span>{peopleLabel}</span></> : null}
          </p>
          <p className="mt-2 hidden text-[15px] text-[var(--cohort-ink-muted)] sm:block">{desktopMeta}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {visiblePeople.length ? (
            <div className="hidden items-center gap-2 sm:flex">
              <span className="flex items-center">
                {visiblePeople.map((person, index) => (
                  <span key={person.id} className={cn("flex", index && "-ml-2.5")}>
                    <CohortAvatar person={person} size="sm" />
                  </span>
                ))}
                {people.length > visiblePeople.length ? (
                  <span className="-ml-2.5 inline-flex h-7 min-w-7 items-center justify-center rounded-full border-2 border-[var(--cohort-paper)] bg-[var(--cohort-muted-fill)] px-1 text-[10px] font-semibold text-[var(--cohort-muted-ink)]">
                    +{people.length - visiblePeople.length}
                  </span>
                ) : null}
              </span>
              {peopleLabel ? <span className="text-[15px] text-[var(--cohort-ink-muted)]">{peopleLabel}</span> : null}
            </div>
          ) : null}
          {actions}
          {onOpenMenu ? (
            <button
              type="button"
              onClick={onOpenMenu}
              className="cohort-icon-button -mr-2.5 flex h-11 w-11 items-center justify-center text-[var(--cohort-ink-muted)] sm:mr-0"
              aria-label="Open cohort navigation"
            >
              <MoreHorizontal className="h-5 w-5" />
            </button>
          ) : null}
        </div>
      </div>
      {timing.progress !== null && startDate && endDate ? (
        <div className="mt-3 sm:hidden">
          <div className="h-0.5 overflow-hidden bg-[var(--cohort-rule)]" aria-label={`${Math.round(timing.progress)}% of the program complete`}>
            <div
              className={cn("h-full", timing.state === "finished" ? "bg-[var(--cohort-radio-idle)]" : "bg-[var(--cohort-forest)]")}
              style={{ width: `${timing.progress}%` }}
            />
          </div>
          <div className="cohort-timeline-caption relative mt-[7px] min-h-6 text-xs leading-6 text-[var(--cohort-ink-subtle)]">
            <span className="absolute left-0 top-0">{formatTimelineDate(startDate)}</span>
            {timing.state === "active" ? (
              <span
                className="absolute top-0 whitespace-nowrap font-medium text-[var(--cohort-ink)]"
                style={{ left: `${clamp(timing.progress, 14, 86)}%`, transform: "translateX(-50%)" }}
              >
                Today · {formatTimelineDate(new Date())}
              </span>
            ) : null}
            <span className="absolute right-0 top-0">{formatTimelineDate(endDate)}</span>
          </div>
        </div>
      ) : timing.state === "missing" ? (
        <div className="mt-3 sm:hidden">
          <p className="text-[15px] leading-6 text-[var(--cohort-ink)]">Start and end dates haven&apos;t been set for this cohort.</p>
          <p className="mt-1 text-sm leading-5 text-[var(--cohort-ink-muted)]">Your administrator adds them; the timeline appears here once they do.</p>
        </div>
      ) : null}
    </header>
  )
}

export function CohortAvatar({
  person,
  size = "md",
  tone = "default",
}: {
  person: Pick<Person, "name" | "initials" | "avatarUrl">
  size?: "sm" | "md" | "lg" | "mobile" | "relationship" | "panel" | "row" | "pairing" | "pairingRow"
  tone?: "default" | "mentor"
}) {
  const sizeClass = {
    sm: "h-7 w-7 text-[10px]",
    md: "h-9 w-9 text-xs",
    lg: "h-11 w-11 text-sm",
    mobile: "h-[34px] w-[34px] text-xs",
    relationship: "h-12 w-12 text-[15px]",
    panel: "h-14 w-14 text-[17px]",
    row: "h-10 w-10 text-[13px]",
    pairing: "h-[30px] w-[30px] text-[11px]",
    pairingRow: "h-[38px] w-[38px] text-[13px]",
  }[size]
  // Initials stay visible while a photo loads and replace it if the photo fails to load.
  return (
    <Avatar className={cn("inline-flex items-center justify-center font-semibold", sizeClass)}>
      {person.avatarUrl ? <AvatarImage src={person.avatarUrl} alt="" className="object-cover" /> : null}
      <AvatarFallback className={tone === "mentor" ? "bg-[var(--cohort-sage)] text-white" : "bg-[var(--cohort-avatar-fill)] text-[var(--cohort-avatar-ink)]"}>
        {person.initials}
      </AvatarFallback>
    </Avatar>
  )
}

type CohortEventHeroProps = {
  viewerTimeZone?: string
  event?: EventsListItem | null
  canCreate?: boolean
  isAdmin?: boolean
  currentUserId?: string | number | null
  roster?: Person[]
  onOpenEvent?: (event: EventsListItem) => void
  onManageEvent?: (event: EventsListItem) => void
  onCreateEvent?: () => void
  onJoinEvent?: (event: EventsListItem) => void
  deferLiveActionOnMobile?: boolean
  className?: string
}

export function CohortEventHero({
  viewerTimeZone,
  event,
  canCreate = false,
  isAdmin = false,
  currentUserId,
  roster = [],
  onOpenEvent,
  onManageEvent,
  onCreateEvent,
  onJoinEvent,
  deferLiveActionOnMobile = false,
  className,
}: CohortEventHeroProps) {
  const start = validDate(event?.startTimeIso)
  const end = validDate(event?.endTimeIso)
  const displayTimeZone = resolveMeetingTimeZone(event?.timeZone, viewerTimeZone)
  const formatTime = (instant: Date) => instant.toLocaleTimeString("en-US", { timeZone: displayTimeZone, hour: "numeric", minute: "2-digit" })
  const now = Date.now()
  const live = isCohortEventLive(event, now)
  const timeLabel = live && end ? `${formatTime(end)} ${meetingTimeZoneLabel(end, displayTimeZone)}` : start ? `${formatTime(start)}${end ? ` – ${formatTime(end)}` : ""} ${meetingTimeZoneLabel(start, displayTimeZone)}` : "Time to be shared"
  const adminIsNotInvited = Boolean(
    event && isAdmin && currentUserId !== null && currentUserId !== undefined
    && !isCurrentUserMeetingParticipant(event, currentUserId),
  )
  const canJoinLiveMeeting = Boolean(live && event?.canJoinVideo && !adminIsNotInvited)
  const attendanceReviewRequired = Boolean(
    isAdmin
    && event?.attendanceTracked === false
    && (event.status === "past" || Boolean(end && end.getTime() <= now && !event.canJoinVideo)),
  )
  const invitees = useMemo(() => {
    const invited = new Set((event?.invitedUserIds || []).map(String))
    const selected = invited.size ? roster.filter((person) => invited.has(String(person.id))) : roster
    return selected.slice(0, 4)
  }, [event?.invitedUserIds, roster])

  const location = event
    ? event.isVirtual && !event.location?.trim()
      ? "Online"
      : event.location?.trim() || "Location to be shared"
    : null

  const primaryLabel = canJoinLiveMeeting ? "Join meeting" : isAdmin ? "Manage event" : "View meeting"
  const recurrenceLabel = event?.recurrence
    ? `Every ${event.recurrence.interval > 1 ? `${event.recurrence.interval} ` : ""}${event.recurrence.period}${event.recurrence.interval > 1 ? "s" : ""}${event.isMilestone ? " · milestone" : ""}`
    : event?.isMilestone
      ? "Program milestone"
      : null
  const supplementaryDetail = isAdmin
    ? { label: "Invited", value: `${Math.max(event?.invitedUserIds?.length || roster.length, invitees.length)} people` }
    : live
      ? { label: "In the room", value: `${event?.activeParticipantCount || 0} people` }
      : event?.recurrence
        ? { label: "Repeats", value: recurrenceLabel }
        : event?.isMilestone
          ? { label: "Type", value: recurrenceLabel }
          : null

  const handlePrimary = () => {
    if (!event) {
      onCreateEvent?.()
      return
    }
    if (canJoinLiveMeeting) {
      onJoinEvent?.(event)
      return
    }
    if (isAdmin) {
      onManageEvent?.(event)
      return
    }
    onOpenEvent?.(event)
  }

  return (
    <section className={cn("cohort-event-hero cohort-mobile-bleed overflow-hidden rounded-[20px] px-5 py-[22px] text-[var(--cohort-hero-ink)] sm:px-10 sm:py-8", className)} aria-labelledby="cohort-next-gathering">
      {event ? (
        <div className="min-w-0">
          <p data-testid="cohort-event-eyebrow-mobile" className="text-sm tracking-[0.02em] text-[var(--cohort-hero-eyebrow)] sm:hidden">
            {live ? "Happening now" : "Next gathering"}{attendanceReviewRequired ? " · attendance review required" : ""}
          </p>
          <div data-testid="cohort-event-layout" className="mt-3.5 grid min-w-0 grid-cols-[max-content_minmax(0,1fr)] items-start gap-x-[18px] gap-y-5 sm:mt-0 sm:grid-cols-[112px_minmax(0,1fr)_240px] sm:items-center sm:gap-x-10">
            <div data-testid="cohort-event-date" className="border-r border-white/25 pr-[18px] text-center sm:pr-9">
              <p className="text-[13px] text-[var(--cohort-hero-muted)] sm:text-[15px]">{start?.toLocaleDateString("en-US", { timeZone: displayTimeZone, weekday: "short" })}</p>
              <p className="cohort-event-date text-[38px] leading-none text-white sm:mt-0.5 sm:text-[72px] sm:leading-[0.95]">{start?.toLocaleDateString("en-US", { timeZone: displayTimeZone, day: "numeric" })}</p>
              <p className="mt-0.5 text-[13px] text-[var(--cohort-hero-muted)] sm:mt-1 sm:text-[15px]">{start?.toLocaleDateString("en-US", { timeZone: displayTimeZone, month: "short" })}</p>
            </div>
            <div data-testid="cohort-event-details" className="min-w-0">
              <p className="hidden text-sm tracking-[0.02em] text-[var(--cohort-hero-eyebrow)] sm:block">
                {live ? "Happening now" : "Next gathering"}{attendanceReviewRequired ? " · attendance review required" : ""}
              </p>
              <h2 id="cohort-next-gathering" className="break-words text-[25px] leading-[1.18] tracking-[-0.01em] text-white sm:mt-2 sm:text-[34px] sm:leading-[1.14] sm:tracking-[-0.02em]">
                {event.title}
              </h2>
              {adminIsNotInvited ? (
                <span data-testid="cohort-event-invitation-status" className="mt-2 inline-flex items-center rounded-full border border-white/25 bg-white/10 px-2.5 py-1 text-xs font-semibold text-[var(--cohort-hero-muted)]">
                  Not invited
                </span>
              ) : null}
              <div className="mt-3 sm:hidden">
                <p className="text-base text-[var(--cohort-hero-muted)]">{live ? "Runs until " : ""}{timeLabel}</p>
                <p className="mt-1 break-words text-[15px] text-[var(--cohort-hero-muted)]">{location}</p>
              </div>
              <div className="mt-4 hidden flex-wrap gap-x-8 gap-y-3 sm:flex">
                <div><p className="text-[13px] text-[var(--cohort-hero-muted)]">{live ? "Runs until" : "Time"}</p><p className="mt-1 text-[17px] text-white">{timeLabel}</p></div>
                <div><p className="text-[13px] text-[var(--cohort-hero-muted)]">Where</p><p className="mt-1 text-[17px] text-white">{location}</p></div>
                {supplementaryDetail ? <div><p className="text-[13px] text-[var(--cohort-hero-muted)]">{supplementaryDetail.label}</p><p className="mt-1 text-[17px] text-white">{supplementaryDetail.value}</p></div> : null}
              </div>
              {invitees.length ? (
                <div className="mt-3 flex flex-wrap items-center gap-2 sm:mt-[18px]">
                  <span className="flex items-center">
                    {invitees.map((person, index) => (
                      <span key={person.id} className={cn("flex", index && "-ml-2")}>
                        <CohortAvatar person={person} size="sm" />
                      </span>
                    ))}
                  </span>
                  <span className="text-sm text-[var(--cohort-hero-muted)]">
                    {event.activeParticipantCount ? `${event.activeParticipantCount} in the room` : `${Math.max(event.invitedUserIds?.length || roster.length, invitees.length)} invited`}
                  </span>
                </div>
              ) : null}
            </div>
            <div className="col-span-2 flex flex-col gap-1 sm:col-span-1">
              <Button type="button" onClick={handlePrimary} className={cn("cohort-hero-button min-h-[52px] rounded-xl bg-[var(--cohort-hero-action)] text-[17px] font-semibold text-[var(--cohort-hero-action-ink)] shadow-none hover:bg-white active:bg-[var(--cohort-hero-action-press)] sm:min-h-14 sm:rounded-[14px]", canJoinLiveMeeting && deferLiveActionOnMobile && "hidden sm:inline-flex")}>
                {primaryLabel}
              </Button>
              {canJoinLiveMeeting || isAdmin ? (
                <button type="button" onClick={() => onOpenEvent?.(event)} className="min-h-11 text-[15px] text-[var(--cohort-hero-muted)] underline underline-offset-4 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-[var(--cohort-hero-action)]">
                  View details
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_15rem] md:items-center">
          <div>
            <p className="text-sm tracking-[0.02em] text-[var(--cohort-hero-eyebrow)]">Next gathering</p>
            <h2 id="cohort-next-gathering" className="mt-2 text-[25px] leading-[1.2] text-white sm:text-[32px]">Nothing on the calendar yet</h2>
            <p className="mt-2 max-w-xl text-[15px] leading-6 text-[var(--cohort-hero-muted)] sm:text-base">
              {canCreate ? "Put the next gathering on the calendar and your cohort will see it here." : "When your mentor sets the next session it lands here first."}
            </p>
          </div>
          {canCreate ? (
            <Button type="button" onClick={handlePrimary} className="cohort-hero-button min-h-[52px] rounded-xl bg-[var(--cohort-hero-action)] text-[17px] font-semibold text-[var(--cohort-hero-action-ink)] shadow-none hover:bg-white active:bg-[var(--cohort-hero-action-press)] sm:min-h-14 sm:rounded-[14px]">
              Create an event
            </Button>
          ) : null}
        </div>
      )}
    </section>
  )
}

export function CohortLoadingState({ admin = false }: { admin?: boolean }) {
  return (
    <div className="cohort-experience cohort-page-surface animate-pulse" role="status" aria-live="polite">
      <span className="sr-only">Loading your cohort</span>
      <div className="space-y-6">
        <div className="space-y-3">
          <div className="h-7 w-60 rounded bg-[var(--cohort-skeleton)]" />
          <div className="h-4 w-40 rounded bg-[var(--cohort-skeleton)]" />
          <div className="h-1 w-full rounded bg-[var(--cohort-skeleton)]" />
        </div>
        <div className="cohort-event-hero cohort-mobile-bleed h-56 rounded-[20px] p-6 sm:mx-0">
          <div className="h-3 w-36 rounded bg-[var(--cohort-hero-skeleton)]" />
          <div className="mt-5 h-8 w-3/5 rounded bg-[var(--cohort-hero-skeleton)]" />
          <div className="mt-3 h-4 w-2/5 rounded bg-[var(--cohort-hero-skeleton)]" />
          <div className="mt-8 h-12 w-full rounded-xl bg-[var(--cohort-hero-skeleton)] sm:ml-auto sm:w-52" />
        </div>
        <div className={cn("grid gap-8", admin ? "lg:grid-cols-2" : "lg:grid-cols-12")}>
          {[0, 1, 2, 3].map((item) => (
            <div key={item} className={cn("space-y-4 border-t border-black/10 pt-4", !admin && "lg:col-span-6")}>
              <div className="h-5 w-36 rounded bg-[var(--cohort-skeleton)]" />
              {[0, 1, 2].map((row) => <div key={row} className="h-12 rounded bg-[var(--cohort-skeleton)]" />)}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function CohortFailureState({ admin = false, onRetry }: { admin?: boolean; onRetry: () => void }) {
  return (
    <div className="cohort-experience cohort-page-surface flex min-h-[55vh] items-center justify-center">
      <div className="w-full max-w-xl border-y border-[var(--cohort-rule)] py-10 text-center" role="alert">
        <AlertTriangle className="mx-auto h-6 w-6 text-[var(--cohort-clay)]" aria-hidden="true" />
        <h1 className="mt-4 text-2xl">{admin ? "We couldn't load this cohort dashboard." : "We couldn't load your cohort."}</h1>
        <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-[var(--cohort-ink-muted)]">
          Something went wrong at our end, not yours. Your tasks and meetings are all still there.
        </p>
        <Button type="button" size="auto" onClick={onRetry} className="cohort-primary-button mt-5 px-7">
          Try again
        </Button>
      </div>
    </div>
  )
}

export function CohortSectionLink({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex min-h-11 items-center gap-1 text-sm font-medium text-[var(--cohort-forest)] hover:underline">
      {children}
      <ChevronRight className="h-4 w-4" />
    </button>
  )
}

export function CohortEmptyScheduleIcon() {
  return <CalendarDays className="h-5 w-5 text-[var(--cohort-hero-eyebrow)]" aria-hidden="true" />
}
